import { TooltipProvider } from "@/components/ui/tooltip";
import { useCallback, useRef } from "react";
import {
  CanvasFloatingToolbarFrame,
  CanvasOutlineMenu,
  CanvasShapeSwapMenu,
  CanvasToolbarDivider,
} from "../components/toolbar";
import type { ShapeStrokeStyle } from "../hooks/useCanvasShapeAndEdgeStyle";
import type { ShapeType } from "./shapes/index";
import { SHAPE_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "./shared/CanvasColorPickerButton";
import { NodeToolbarContextMenuButton } from "./shared/NodeToolbarContextMenuButton";

interface ShapeFloatingToolbarProps {
  shapeType: ShapeType;
  fillColor: string;
  noFill: boolean;
  strokeStyle: ShapeStrokeStyle;
  onShapeTypeChange: (shape: ShapeType) => void;
  onColorChange: (color: string) => void;
  onNoFillChange: (noFill: boolean) => void;
  onStrokeStyleChange: (style: ShapeStrokeStyle) => void;
  onPopoverOpenChange?: (isOpen: boolean) => void;
}

export function ShapeFloatingToolbar({
  shapeType,
  fillColor,
  noFill,
  strokeStyle,
  onShapeTypeChange,
  onColorChange,
  onNoFillChange,
  onStrokeStyleChange,
  onPopoverOpenChange,
}: ShapeFloatingToolbarProps) {
  const isColorOpenRef = useRef(false);
  const isOutlineOpenRef = useRef(false);
  const isSwapOpenRef = useRef(false);

  const notifyOpen = useCallback(
    (open: boolean) => {
      onPopoverOpenChange?.(
        isColorOpenRef.current ||
          isOutlineOpenRef.current ||
          isSwapOpenRef.current ||
          open,
      );
    },
    [onPopoverOpenChange],
  );

  const handleColorOpen = useCallback(
    (open: boolean) => {
      isColorOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  const handleOutlineOpen = useCallback(
    (open: boolean) => {
      isOutlineOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  const handleSwapOpen = useCallback(
    (open: boolean) => {
      isSwapOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  return (
    <TooltipProvider delayDuration={300}>
      <CanvasFloatingToolbarFrame onMouseDown={(e) => e.preventDefault()}>
        <CanvasShapeSwapMenu
          currentShape={shapeType}
          onShapeChange={onShapeTypeChange}
          onOpenChange={handleSwapOpen}
        />

        <CanvasColorPickerButton
          colors={SHAPE_COLORS}
          activeValue={fillColor}
          noFill={noFill}
          tooltip="Fill color"
          onColorChange={onColorChange}
          onNoFillChange={onNoFillChange}
          onOpenChange={handleColorOpen}
        />

        <CanvasOutlineMenu
          strokeStyle={strokeStyle}
          onStrokeStyleChange={onStrokeStyleChange}
          onOpenChange={handleOutlineOpen}
        />

        <CanvasToolbarDivider />

        <NodeToolbarContextMenuButton />
      </CanvasFloatingToolbarFrame>
    </TooltipProvider>
  );
}
